import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Save, Megaphone, Lock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import PageLoadingState from '../components/PageLoadingState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSettings() {
  const [settings, setSettings] = useState({ 
    razorpay_enabled: true,
    announcement_message: '',
    announcement_enabled: false,
    referral_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        razorpay_enabled: response.data.razorpay_enabled ?? true,
        announcement_message: response.data.announcement_message ?? '',
        announcement_enabled: response.data.announcement_enabled ?? false,
        referral_enabled: response.data.referral_enabled ?? true
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = sessionStorage.getItem('token');
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    const token = sessionStorage.getItem('token');
    setChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password updated successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <PageLoadingState title="Loading admin settings" />
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">Settings</h1>
              <p className="text-base md:text-lg text-muted-foreground">Configure store-wide options</p>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="rounded-full px-8 py-6 bg-primary hover:bg-primary/90 text-white shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all duration-300 w-fit"
            >
              {saving ? 'Saving...' : (
                <span className="flex items-center gap-2">
                  <Save className="h-5 w-5" />
                  Save Changes
                </span>
              )}
            </Button>
          </div>

          <div className="space-y-6">
            <div className="glass-morphic rounded-2xl p-6 md:p-8 border border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Megaphone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">Announcement Bar</h3>
                    <p className="text-muted-foreground">Display a running message on the homepage for offers or updates</p>
                  </div>
                </div>
                <Switch 
                  checked={settings.announcement_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, announcement_enabled: checked })}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="announcement_message">Announcement Message</Label>
                <Input 
                  id="announcement_message"
                  placeholder="e.g. Free shipping on orders over ₹999! 🎉"
                  value={settings.announcement_message}
                  onChange={(e) => setSettings({ ...settings, announcement_message: e.target.value })}
                  className="bg-white/5 border-white/10 focus:border-primary/50"
                />
              </div>
            </div>

            <div className="glass-morphic rounded-2xl p-6 md:p-8 border border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">Razorpay Payments</h3>
                    <p className="text-muted-foreground">Allow customers to pay using Razorpay (UPI, Cards, Netbanking)</p>
                  </div>
                </div>
                <Switch 
                  checked={settings.razorpay_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, razorpay_enabled: checked })}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
            </div>

            <div className="glass-morphic rounded-2xl p-6 md:p-8 border border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">Referral Program</h3>
                    <p className="text-muted-foreground">Enable or disable the entire referral and rewards system</p>
                  </div>
                </div>
                <Switch 
                  checked={settings.referral_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, referral_enabled: checked })}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
            </div>

            <div className="glass-morphic rounded-2xl p-6 md:p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Lock className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">Change Admin Password</h3>
                  <p className="text-muted-foreground">Secure your admin account by updating your password</p>
                </div>
              </div>
              
              <form onSubmit={handleChangePassword} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input 
                      id="current_password"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      className="bg-white/5 border-white/10 focus:border-primary/50"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input 
                      id="new_password"
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      className="bg-white/5 border-white/10 focus:border-primary/50"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input 
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      className="bg-white/5 border-white/10 focus:border-primary/50"
                      required
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  disabled={changingPassword}
                  className="rounded-full px-8 py-4 bg-primary hover:bg-primary/90 text-white transition-all duration-300"
                >
                  {changingPassword ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
